import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { get, post } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";
import {
  ONBOARDING_IDENTITY,
  type OnboardingIdentity,
} from "../../Utils/Constants/RoleConstants";
import { toast } from "../../Utils/Helper/toast";

export interface EasyOnboardingSettings {
  invite_expiry_hours: number;
  reminder_cooldown_hours: number;
  max_invites_per_day: number;
}

export interface EasyOnboardingConfig {
  id?: string;
  key: string;
  value: EasyOnboardingSettings;
  created_at?: string;
  updated_at?: string;
}

export interface OnboardingInvitation {
  id: string;
  invited_email: string;
  invited_name?: string | null;
  invited_title?: string | null;
  company_identity_hint?: string | null;
  program_role_hint?: string | null;
  team_role_hint?: string | null;
  claimed_team_role_hint?: string | null;
  invited_grade?: number | null;
  status: string;
  token_expires_at: string;
  invited_by?: string | null;
  invited_by_name?: string | null;
  inviter_role?: string | null;
  accepted_by_user_id?: string | null;
  accepted_at?: string | null;
  reminded_count?: number | null;
  last_reminded_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  metadata?: Record<string, unknown>;
}

export interface SendOnboardingInvitationPayload {
  invited_email: string;
  invited_name: string;
  invited_title: string;
  company_identity_hint: OnboardingIdentity;
  invited_grade: number;
  team_role_hint?: string;
}

export interface ExistingUserInviteConflict {
  code: "USER_ALREADY_EXISTS";
  user_id: string;
  full_name: string;
  email: string;
  role: string;
  last_login_at: string | null;
  has_logged_in: boolean;
  message?: string;
}

export const isExistingNeverLoggedInConflict = (
  payload: unknown
): payload is ExistingUserInviteConflict =>
  typeof payload === "object" &&
  payload !== null &&
  (payload as ExistingUserInviteConflict).code === "USER_ALREADY_EXISTS" &&
  (payload as ExistingUserInviteConflict).has_logged_in === false;

export interface SendExistingUserPortfolioResult {
  success: boolean;
  welcome_email_sent: boolean;
  portfolio_email_sent: boolean;
  account_names: string[];
  account_count: number;
}

type SendInvitationApiBody =
  | {
      invited_email: string;
      invited_name: string;
      invited_title: string;
      company_identity_hint: typeof ONBOARDING_IDENTITY.ACCOUNT_TEAM;
      team_role_hint: string;
    }
  | {
      invited_email: string;
      invited_name: string;
      invited_title: string;
      company_identity_hint:
        | typeof ONBOARDING_IDENTITY.C_LEVEL
        | typeof ONBOARDING_IDENTITY.EXECUTIVE;
      invited_grade: number;
    };

const apiErrorMessage = (data: any, fallback: string): string => {
  try {
    const fromThrown = JSON.parse(data?.message)?.message;
    if (fromThrown) return fromThrown;
  } catch {
    // Not an ApiRequest JSON error payload.
  }
  const envelopeMessage =
    data?.response?.data?.response?.message ?? data?.response?.data?.message;
  if (typeof envelopeMessage === "string" && envelopeMessage.trim()) {
    return envelopeMessage;
  }
  const detail = data?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0];
    const msg = typeof first === "string" ? first : first?.msg;
    if (msg) return msg;
  }
  return data?.response?.message ?? fallback;
};

export interface WizardInvite {
  id: string;
  invited_email: string;
  invited_name?: string | null;
  invited_title?: string | null;
  invited_by_name?: string | null;
  company_identity_hint?: string | null;
  program_role_hint?: string | null;
  team_role_hint?: string | null;
  claimed_team_role_hint?: string | null;
  token_expires_at?: string;
  status: string;
}

export interface WizardOrganization {
  id: string;
  name: string;
  domain: string;
  slug?: string | null;
  company_name?: string | null;
  schema_name?: string | null;
}

export interface WizardAccount {
  id: string;
  name: string;
}

export interface WizardProfilePayload {
  email: string;
  name: string;
  title: string;
  phone?: string;
  languages?: string;
}

export interface WizardAssociationPayload {
  account_id: string;
  association_type: string;
  is_account_team: boolean;
}

export interface CompleteOnboardingWizardPayload {
  token: string;
  profile: WizardProfilePayload;
  associations: WizardAssociationPayload[];
  monitoring_account_ids?: string[];
  password?: string;
  confirm_password?: string;
}

interface VerifyWizardResponse {
  valid: boolean;
  invite?: WizardInvite | null;
  organization?: WizardOrganization | null;
  error?: string | null;
}

interface WizardAccountsResponse {
  accounts: WizardAccount[];
}

interface CompleteWizardResponse {
  success: boolean;
  message: string;
  user_id?: string;
}

interface LoadWizardResult {
  invite: WizardInvite | null;
  organization: WizardOrganization | null;
  tenantDomain: string;
  accounts: WizardAccount[];
}

const onboardingWizardRequest = async <T>({
  method,
  url,
  data,
  tenantDomain,
}: {
  method: "GET" | "POST";
  url: string;
  data?: unknown;
  tenantDomain?: string | null;
}): Promise<T> => {
  const options = {
    headers: tenantDomain
      ? { "X-Tenant-Domain": tenantDomain }
      : ({} as Record<string, string>),
  };

  if (method === "GET") {
    return (await get(url, options)) as T;
  }

  return (await post(url, data, false, options)) as T;
};

export const buildSendInvitationRequestBody = (
  payload: SendOnboardingInvitationPayload
): SendInvitationApiBody => {
  const base = {
    invited_email: payload.invited_email.trim(),
    invited_name: payload.invited_name.trim(),
    invited_title: payload.invited_title.trim(),
  };

  if (payload.company_identity_hint === ONBOARDING_IDENTITY.ACCOUNT_TEAM) {
    return {
      ...base,
      company_identity_hint: ONBOARDING_IDENTITY.ACCOUNT_TEAM,
      team_role_hint: (payload.team_role_hint || "").trim().toUpperCase(),
    };
  }

  return {
    ...base,
    company_identity_hint: payload.company_identity_hint,
    invited_grade: Number(payload.invited_grade),
  };
};

const DEFAULT_SETTINGS: EasyOnboardingSettings = {
  invite_expiry_hours: 168,
  reminder_cooldown_hours: 24,
  max_invites_per_day: 200,
};

const normalizeSettings = (
  value?: Partial<EasyOnboardingSettings> | null
): EasyOnboardingSettings => ({
  invite_expiry_hours: Math.max(
    1,
    Number(value?.invite_expiry_hours) || DEFAULT_SETTINGS.invite_expiry_hours
  ),
  reminder_cooldown_hours: Math.max(
    1,
    Number(value?.reminder_cooldown_hours) ||
      DEFAULT_SETTINGS.reminder_cooldown_hours
  ),
  max_invites_per_day: Math.max(
    1,
    Number(value?.max_invites_per_day) || DEFAULT_SETTINGS.max_invites_per_day
  ),
});

interface EasyOnboardingState {
  config: EasyOnboardingSettings;
  configId: string | null;
  configKey: string | null;
  configLoading: boolean;
  configError: string | null;
  configUpdateLoading: boolean;
  configUpdateError: string | null;
  invitations: OnboardingInvitation[];
  invitationsLoading: boolean;
  invitationsError: string | null;
  sendInviteLoading: boolean;
  sendInviteError: string | null;
  sendWelcomeLoading: boolean;
  sendWelcomeError: string | null;
  sendPortfolioLoading: boolean;
  sendPortfolioError: string | null;
  remindLoading: boolean;
  remindError: string | null;
  wizardLoading: boolean;
  wizardError: string | null;
  wizardInvite: WizardInvite | null;
  wizardOrganization: WizardOrganization | null;
  wizardTenantDomain: string;
  wizardAccounts: WizardAccount[];
  wizardCompleteLoading: boolean;
  wizardCompleteError: string | null;
}

const initialState: EasyOnboardingState = {
  config: DEFAULT_SETTINGS,
  configId: null,
  configKey: "easy_onbording",
  configLoading: false,
  configError: null,
  configUpdateLoading: false,
  configUpdateError: null,
  invitations: [],
  invitationsLoading: false,
  invitationsError: null,
  sendInviteLoading: false,
  sendInviteError: null,
  sendWelcomeLoading: false,
  sendWelcomeError: null,
  sendPortfolioLoading: false,
  sendPortfolioError: null,
  remindLoading: false,
  remindError: null,
  wizardLoading: false,
  wizardError: null,
  wizardInvite: null,
  wizardOrganization: null,
  wizardTenantDomain: "",
  wizardAccounts: [],
  wizardCompleteLoading: false,
  wizardCompleteError: null,
};

export const fetchEasyOnboardingConfig = createAsyncThunk<
  EasyOnboardingConfig,
  void,
  { rejectValue: string }
>("easyOnboarding/fetchConfig", async (_, { rejectWithValue }) => {
  try {
    const response = (await get(API_URLS.fetchEasyOnboardingConfig())) as {
      status?: string;
      message?: string;
      content?: EasyOnboardingConfig;
    } & EasyOnboardingConfig;

    if (response?.status === "failure" || response?.status === "unauthorized") {
      return rejectWithValue(
        apiErrorMessage(response, "Failed to fetch onboarding settings")
      );
    }

    const payload = (response?.content ?? response) as EasyOnboardingConfig;
    if (!payload?.key && !payload?.value) {
      return rejectWithValue("Onboarding settings config is missing");
    }

    return {
      id: payload.id,
      key: payload.key || "easy_onbording",
      value: normalizeSettings(payload.value),
      created_at: payload.created_at,
      updated_at: payload.updated_at,
    };
  } catch (error: any) {
    return rejectWithValue(
      apiErrorMessage(error, "Failed to fetch onboarding settings")
    );
  }
});

export const updateEasyOnboardingConfig = createAsyncThunk<
  EasyOnboardingConfig,
  { key: string; value: EasyOnboardingSettings },
  { rejectValue: string }
>(
  "easyOnboarding/updateConfig",
  async ({ key, value }, { rejectWithValue }) => {
    try {
      const response = (await post(API_URLS.upsertVendorConfig(), {
        key,
        value: normalizeSettings(value),
      })) as {
        status?: string;
        message?: string;
        content?: EasyOnboardingConfig;
      } & EasyOnboardingConfig;

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          apiErrorMessage(response, "Failed to save onboarding settings")
        );
      }

      const payload = (response?.content ?? response) as EasyOnboardingConfig;
      toast.success("Onboarding settings updated successfully.");
      return {
        id: payload?.id,
        key: payload?.key || key,
        value: normalizeSettings(payload?.value ?? value),
        created_at: payload?.created_at,
        updated_at: payload?.updated_at,
      };
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to save onboarding settings")
      );
    }
  }
);

export const fetchOnboardingInvitations = createAsyncThunk<
  OnboardingInvitation[],
  void,
  { rejectValue: string }
>("easyOnboarding/fetchInvitations", async (_, { rejectWithValue }) => {
  try {
    const response = (await get(API_URLS.fetchInvitations())) as
      | OnboardingInvitation[]
      | {
          status?: string;
          message?: string;
          content?: OnboardingInvitation[];
        };

    if (
      !Array.isArray(response) &&
      (response?.status === "failure" || response?.status === "unauthorized")
    ) {
      return rejectWithValue(
        apiErrorMessage(response, "Failed to fetch invitations")
      );
    }

    const payload = Array.isArray(response)
      ? response
      : (response?.content ?? []);
    return payload;
  } catch (error: any) {
    return rejectWithValue(
      apiErrorMessage(error, "Failed to fetch invitations")
    );
  }
});

export const sendOnboardingInvitation = createAsyncThunk<
  OnboardingInvitation,
  SendOnboardingInvitationPayload,
  { rejectValue: string | ExistingUserInviteConflict }
>("easyOnboarding/sendInvitation", async (payload, { rejectWithValue }) => {
  const parseThrownError = (error: any) => {
    try {
      return JSON.parse(error?.message);
    } catch {
      return null;
    }
  };

  const rejectExistingUser = (content: any, message: string) => {
    if (content?.code !== "USER_ALREADY_EXISTS") {
      return null;
    }
    if (content.has_logged_in) {
      return rejectWithValue(
        message || "User already exists in this organization"
      );
    }
    return rejectWithValue({
      code: "USER_ALREADY_EXISTS" as const,
      user_id: String(content.user_id || ""),
      full_name: content.full_name || "",
      email: content.email || "",
      role: content.role || "Unknown",
      last_login_at: content.last_login_at ?? null,
      has_logged_in: false,
      message: message || "User already exists in this organization",
    });
  };

  try {
    const requestBody = buildSendInvitationRequestBody(payload);
    const response = (await post(
      API_URLS.sendOnboardingInvitation(),
      requestBody
    )) as {
      success?: boolean;
      invite?: OnboardingInvitation;
      status?: string;
      message?: string;
      content?: {
        success?: boolean;
        invite?: OnboardingInvitation;
        code?: string;
        has_logged_in?: boolean;
      };
      response?: { message?: string };
    };

    if (response?.status === "failure" || response?.status === "unauthorized") {
      const existingReject = rejectExistingUser(
        response?.content,
        apiErrorMessage(response, "Failed to send onboarding invitation")
      );
      if (existingReject) return existingReject;
      return rejectWithValue(
        apiErrorMessage(response, "Failed to send onboarding invitation")
      );
    }

    const invite = response?.invite ?? response?.content?.invite;
    if (!invite) {
      return rejectWithValue("Invitation response is missing");
    }

    toast.success("Onboarding invitation sent successfully.");
    return invite;
  } catch (error: any) {
    const parsed = parseThrownError(error);
    const existingReject = rejectExistingUser(
      parsed?.content,
      parsed?.message ||
        apiErrorMessage(error, "Failed to send onboarding invitation")
    );
    if (existingReject) return existingReject;
    return rejectWithValue(
      apiErrorMessage(error, "Failed to send onboarding invitation")
    );
  }
});

type SendExistingUserEmailPayload = {
  user_id: string;
  include_portfolio_email: boolean;
};

const postExistingUserEmail = async (
  payload: SendExistingUserEmailPayload
): Promise<{
  result?: SendExistingUserPortfolioResult;
  error?: string;
}> => {
  try {
    const response = (await post(
      API_URLS.sendExistingUserPortfolioInvite(),
      payload
    )) as {
      success?: boolean;
      welcome_email_sent?: boolean;
      portfolio_email_sent?: boolean;
      account_names?: string[];
      account_count?: number;
      status?: string;
      message?: string;
      content?: SendExistingUserPortfolioResult;
    };

    if (response?.status === "failure" || response?.status === "unauthorized") {
      return {
        error: apiErrorMessage(
          response,
          payload.include_portfolio_email
            ? "Failed to send welcome and portfolio emails"
            : "Failed to send welcome email"
        ),
      };
    }

    const result: SendExistingUserPortfolioResult = {
      success: Boolean(response?.success ?? response?.content?.success ?? true),
      welcome_email_sent: Boolean(
        response?.welcome_email_sent ?? response?.content?.welcome_email_sent
      ),
      portfolio_email_sent: Boolean(
        response?.portfolio_email_sent ??
          response?.content?.portfolio_email_sent
      ),
      account_names:
        response?.account_names ?? response?.content?.account_names ?? [],
      account_count:
        response?.account_count ?? response?.content?.account_count ?? 0,
    };

    if (!payload.include_portfolio_email) {
      toast.success("Welcome email sent successfully.");
    } else if (result.portfolio_email_sent) {
      toast.success("Welcome and portfolio emails sent successfully.");
    } else {
      toast.success(
        "Welcome email sent. No associated accounts were found for a portfolio email."
      );
    }
    return { result };
  } catch (error: any) {
    return {
      error: apiErrorMessage(
        error,
        payload.include_portfolio_email
          ? "Failed to send welcome and portfolio emails"
          : "Failed to send welcome email"
      ),
    };
  }
};

export const sendExistingUserWelcomeInvite = createAsyncThunk<
  SendExistingUserPortfolioResult,
  { user_id: string },
  { rejectValue: string }
>(
  "easyOnboarding/sendExistingUserWelcome",
  async (payload, { rejectWithValue }) => {
    const { result, error } = await postExistingUserEmail({
      user_id: payload.user_id,
      include_portfolio_email: false,
    });
    if (error || !result) {
      return rejectWithValue(error ?? "Failed to send welcome email");
    }
    return result;
  }
);

export const sendExistingUserPortfolioInvite = createAsyncThunk<
  SendExistingUserPortfolioResult,
  { user_id: string },
  { rejectValue: string }
>(
  "easyOnboarding/sendExistingUserPortfolio",
  async (payload, { rejectWithValue }) => {
    const { result, error } = await postExistingUserEmail({
      user_id: payload.user_id,
      include_portfolio_email: true,
    });
    if (error || !result) {
      return rejectWithValue(
        error ?? "Failed to send welcome and portfolio emails"
      );
    }
    return result;
  }
);

export const sendInvitationReminder = createAsyncThunk<
  OnboardingInvitation,
  string,
  { rejectValue: string }
>("easyOnboarding/sendReminder", async (invitationId, { rejectWithValue }) => {
  try {
    const response = (await post(API_URLS.sendInvitationReminder(), {
      invitation_id: invitationId,
    })) as {
      success?: boolean;
      invite?: OnboardingInvitation;
      mode?: string;
      status?: string;
      message?: string;
      content?: { invite?: OnboardingInvitation };
    };

    if (response?.status === "failure" || response?.status === "unauthorized") {
      return rejectWithValue(
        apiErrorMessage(response, "Failed to send reminder")
      );
    }

    const invite = response?.invite ?? response?.content?.invite;
    if (!invite) {
      return rejectWithValue("Reminder response is missing");
    }

    const modeLabel =
      response?.mode === "reissued" ? "Invite reissued" : "Reminder sent";
    toast.success(`${modeLabel} successfully.`);
    return invite;
  } catch (error: any) {
    return rejectWithValue(apiErrorMessage(error, "Failed to send reminder"));
  }
});

export const loadOnboardingWizard = createAsyncThunk<
  LoadWizardResult,
  { token: string; tenantDomain?: string },
  { rejectValue: string }
>(
  "easyOnboarding/loadWizard",
  async ({ token, tenantDomain }, { rejectWithValue }) => {
    try {
      const verifyRaw = await onboardingWizardRequest<
        VerifyWizardResponse | { content: VerifyWizardResponse }
      >({
        method: "GET",
        url: API_URLS.verifyOnboardingWizard({ token }),
        tenantDomain: tenantDomain || null,
      });

      const verifyResponse =
        (verifyRaw as { content?: VerifyWizardResponse })?.content ??
        (verifyRaw as VerifyWizardResponse);

      if (!verifyResponse.valid) {
        return rejectWithValue(
          verifyResponse.error || "Invalid or expired invitation"
        );
      }

      const resolvedTenant =
        tenantDomain || verifyResponse.organization?.domain || "";

      const accountsRaw = await onboardingWizardRequest<
        WizardAccountsResponse | { content: WizardAccountsResponse }
      >({
        method: "GET",
        url: API_URLS.fetchOnboardingWizardAccounts({ token }),
        tenantDomain: resolvedTenant || null,
      });

      const accountsResponse =
        (accountsRaw as { content?: WizardAccountsResponse })?.content ??
        (accountsRaw as WizardAccountsResponse);

      return {
        invite: verifyResponse.invite || null,
        organization: verifyResponse.organization || null,
        tenantDomain: resolvedTenant,
        accounts: accountsResponse.accounts || [],
      };
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Invalid or expired invitation")
      );
    }
  }
);

export const completeOnboardingWizard = createAsyncThunk<
  CompleteWizardResponse,
  { payload: CompleteOnboardingWizardPayload; tenantDomain?: string },
  { rejectValue: string }
>(
  "easyOnboarding/completeWizard",
  async ({ payload, tenantDomain }, { rejectWithValue }) => {
    try {
      const raw = await onboardingWizardRequest<
        CompleteWizardResponse | { content: CompleteWizardResponse }
      >({
        method: "POST",
        url: API_URLS.completeOnboardingWizard(),
        data: payload,
        tenantDomain: tenantDomain || null,
      });

      return (
        (raw as { content?: CompleteWizardResponse })?.content ??
        (raw as CompleteWizardResponse)
      );
    } catch (error: any) {
      return rejectWithValue(
        apiErrorMessage(error, "Failed to complete onboarding")
      );
    }
  }
);

const easyOnboardingSlice = createSlice({
  name: "easyOnboarding",
  initialState,
  reducers: {
    clearSendInviteError: state => {
      state.sendInviteError = null;
    },
    clearRemindError: state => {
      state.remindError = null;
    },
    clearWizardState: state => {
      state.wizardLoading = false;
      state.wizardError = null;
      state.wizardInvite = null;
      state.wizardOrganization = null;
      state.wizardTenantDomain = "";
      state.wizardAccounts = [];
      state.wizardCompleteLoading = false;
      state.wizardCompleteError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchEasyOnboardingConfig.pending, state => {
        state.configLoading = true;
        state.configError = null;
      })
      .addCase(fetchEasyOnboardingConfig.fulfilled, (state, action) => {
        state.configLoading = false;
        state.config = action.payload.value;
        state.configId = action.payload.id ?? null;
        state.configKey = action.payload.key;
      })
      .addCase(fetchEasyOnboardingConfig.rejected, (state, action) => {
        state.configLoading = false;
        state.configError = action.payload as string;
      })
      .addCase(updateEasyOnboardingConfig.pending, state => {
        state.configUpdateLoading = true;
        state.configUpdateError = null;
      })
      .addCase(updateEasyOnboardingConfig.fulfilled, (state, action) => {
        state.configUpdateLoading = false;
        state.config = action.payload.value;
        state.configId = action.payload.id ?? null;
        state.configKey = action.payload.key;
      })
      .addCase(updateEasyOnboardingConfig.rejected, (state, action) => {
        state.configUpdateLoading = false;
        state.configUpdateError = action.payload as string;
      })
      .addCase(fetchOnboardingInvitations.pending, state => {
        state.invitationsLoading = true;
        state.invitationsError = null;
      })
      .addCase(fetchOnboardingInvitations.fulfilled, (state, action) => {
        state.invitationsLoading = false;
        state.invitations = action.payload;
      })
      .addCase(fetchOnboardingInvitations.rejected, (state, action) => {
        state.invitationsLoading = false;
        state.invitationsError = action.payload as string;
      })
      .addCase(sendOnboardingInvitation.pending, state => {
        state.sendInviteLoading = true;
        state.sendInviteError = null;
      })
      .addCase(sendOnboardingInvitation.fulfilled, (state, action) => {
        state.sendInviteLoading = false;
        const existingIndex = state.invitations.findIndex(
          invite => invite.id === action.payload.id
        );
        if (existingIndex >= 0) {
          state.invitations[existingIndex] = action.payload;
        } else {
          state.invitations = [action.payload, ...state.invitations];
        }
      })
      .addCase(sendOnboardingInvitation.rejected, (state, action) => {
        state.sendInviteLoading = false;
        if (isExistingNeverLoggedInConflict(action.payload)) {
          state.sendInviteError = null;
          return;
        }
        const message =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to send onboarding invitation";
        state.sendInviteError = message;
        toast.error(message);
      })
      .addCase(sendExistingUserWelcomeInvite.pending, state => {
        state.sendWelcomeLoading = true;
        state.sendWelcomeError = null;
      })
      .addCase(sendExistingUserWelcomeInvite.fulfilled, state => {
        state.sendWelcomeLoading = false;
      })
      .addCase(sendExistingUserWelcomeInvite.rejected, (state, action) => {
        state.sendWelcomeLoading = false;
        state.sendWelcomeError = action.payload as string;
        toast.error(action.payload as string);
      })
      .addCase(sendExistingUserPortfolioInvite.pending, state => {
        state.sendPortfolioLoading = true;
        state.sendPortfolioError = null;
      })
      .addCase(sendExistingUserPortfolioInvite.fulfilled, state => {
        state.sendPortfolioLoading = false;
      })
      .addCase(sendExistingUserPortfolioInvite.rejected, (state, action) => {
        state.sendPortfolioLoading = false;
        state.sendPortfolioError = action.payload as string;
        toast.error(action.payload as string);
      })
      .addCase(sendInvitationReminder.pending, state => {
        state.remindLoading = true;
        state.remindError = null;
      })
      .addCase(sendInvitationReminder.fulfilled, (state, action) => {
        state.remindLoading = false;
        const existingIndex = state.invitations.findIndex(
          invite => invite.id === action.payload.id
        );
        if (existingIndex >= 0) {
          state.invitations[existingIndex] = action.payload;
        }
      })
      .addCase(sendInvitationReminder.rejected, (state, action) => {
        state.remindLoading = false;
        state.remindError = action.payload as string;
        toast.error(action.payload as string);
      })
      .addCase(loadOnboardingWizard.pending, state => {
        state.wizardLoading = true;
        state.wizardError = null;
      })
      .addCase(loadOnboardingWizard.fulfilled, (state, action) => {
        state.wizardLoading = false;
        state.wizardInvite = action.payload.invite;
        state.wizardOrganization = action.payload.organization;
        state.wizardTenantDomain = action.payload.tenantDomain;
        state.wizardAccounts = action.payload.accounts;
      })
      .addCase(loadOnboardingWizard.rejected, (state, action) => {
        state.wizardLoading = false;
        state.wizardError = action.payload as string;
      })
      .addCase(completeOnboardingWizard.pending, state => {
        state.wizardCompleteLoading = true;
        state.wizardCompleteError = null;
      })
      .addCase(completeOnboardingWizard.fulfilled, state => {
        state.wizardCompleteLoading = false;
      })
      .addCase(completeOnboardingWizard.rejected, (state, action) => {
        state.wizardCompleteLoading = false;
        state.wizardCompleteError = action.payload as string;
      });
  },
});

export const { clearSendInviteError, clearRemindError, clearWizardState } =
  easyOnboardingSlice.actions;
export default easyOnboardingSlice.reducer;
